/**
 * The five agent templates (docs/ARCHITECTURE-V4.md §8): the starting descriptions the desk and the creation interview
 * offer. The develop loop's research record, algorithms and execution facts that this module described went with lane
 * BOO2 (research and execution are the platform's).
 */
import { z } from 'zod'
import type { PermissionCapability, RiskProfile } from '../enums.js'

export const AgentTemplateId = z.enum(['trend_scout', 'wallet_hunter', 'breakout_trader', 'lp_manager', 'launch_agent'])
export type AgentTemplateId = z.output<typeof AgentTemplateId>

export interface AgentTemplateInfo {
  id: AgentTemplateId
  name: string
  tagline: string
  description: string
  /** The algorithms the template names (display keys; the platform runs the strategy, lane BOO2). */
  algorithms: readonly string[]
  permissions: readonly z.output<typeof PermissionCapability>[]
  workflows: readonly string[]
  comingSoon: boolean
  riskProfile: z.output<typeof RiskProfile>
}

export const AGENT_TEMPLATES: readonly AgentTemplateInfo[] = [
  {
    id: 'trend_scout',
    name: 'Trend Scout',
    tagline: 'Finds early trends with improving liquidity and independent buyers.',
    description: 'Keeps a ranked watchlist of emerging tokens, enters on conditions it has tested, stands aside in bad regimes and trails exits.',
    algorithms: ['emerging_token_ranker', 'market_regime', 'liquidity_deterioration'],
    permissions: ['spot', 'protective'],
    workflows: ['Watchlist', 'Conditional entries', 'Trailing exits'],
    comingSoon: false,
    riskProfile: 'balanced',
  },
  {
    id: 'wallet_hunter',
    name: 'Wallet Hunter',
    tagline: 'Copies wallets whose results survive copy delay and costs.',
    description: 'Maintains a leader list of copyable wallets, weights their agreement by independence, copies with lots and exits when a leader sells or moves tokens out.',
    algorithms: ['copyable_wallet_finder', 'independent_wallet_consensus', 'execution_optimiser'],
    permissions: ['copy', 'protective'],
    workflows: ['Leader list maintenance', 'Copy with lots', 'Exit on leader sell or transfer-out'],
    comingSoon: false,
    riskProfile: 'balanced',
  },
  {
    id: 'breakout_trader',
    name: 'Breakout Trader',
    tagline: 'Trades volume-confirmed breakouts and takes profit in steps.',
    description: 'Enters confirmed breakouts when the regime allows, scales in on strength and takes profit in steps.',
    algorithms: ['momentum_breakout', 'market_regime'],
    permissions: ['spot', 'protective', 'schedule'],
    workflows: ['Breakout entries', 'Scale-ins', 'Stepped profit-taking'],
    comingSoon: false,
    riskProfile: 'degen',
  },
  {
    id: 'lp_manager',
    name: 'LP Manager',
    tagline: 'Provides liquidity where fees pay for the risk.',
    description: 'Will allocate to pools and ranges by fees per unit of inventory risk, collect, rebalance and withdraw when liquidity deteriorates, and report performance against simply holding.',
    algorithms: ['lp_range_optimiser', 'liquidity_deterioration'],
    permissions: ['lp'],
    workflows: ['Allocate', 'Collect', 'Rebalance', 'Withdraw', 'Performance versus holding'],
    comingSoon: true,
    riskProfile: 'conservative',
  },
  {
    id: 'launch_agent',
    name: 'Launch Agent',
    tagline: 'Launches tokens and trades new pools it rates well.',
    description: 'Will run a token launch end to end (launch saga, initial liquidity, launch fee collection) and qualify new pools before any entry.',
    algorithms: ['new_pool_quality'],
    permissions: ['launch', 'spot'],
    workflows: ['Launch saga', 'Initial liquidity', 'Launch fee collection'],
    comingSoon: true,
    riskProfile: 'degen',
  },
]
